import { THREE, gsap } from '@powerplay/common';
import { ParticleObject } from '.';
import { EaseValuesType } from './types';
export class ParticleEmitter {
    minSettingsQualityForRender = 7;
    oneTimeEffect = false;
    enabled = true;
    activeParticles = [];
    active = true;
    frames = 0;
    activeParticle;
    offset;
    repeat;
    maxParticleAmount;
    emitRate;
    name;
    gravitation;
    gravitationAcceleration = new THREE.Vector3(0, -9.81, 0);
    randomDrift;
    randomizeRotationDir;
    velocity;
    velocitySlowDown;
    easeValues;
    rotation;
    rotationDir;
    rotationCoefs;
    randomPower;
    alpha;
    mass;
    scale;
    particleAmountPerEmit;
    position;
    span;
    lastEmition = 0;
    particleLife;
    particleOptions;
    fpsLimit;
    alphaLinearVanishTime;
    alphaLinearVanishTimeDeviation;
    spawnTimeDeviationLimit;
    constructor(optionsCopy) {
        this.emitRate = optionsCopy.emitRate;
        this.particleLife = optionsCopy.particleLife;
        this.maxParticleAmount = optionsCopy.maxParticleAmount;
        this.particleAmountPerEmit = optionsCopy.particleAmountPerEmit;
        this.span = optionsCopy.particleSpan;
        this.position = optionsCopy.basePosition;
        this.rotation = optionsCopy.rotation;
        this.rotationCoefs = optionsCopy.rotationCoefs;
        this.rotationDir = optionsCopy.rotationDir;
        this.alpha = optionsCopy.alpha;
        this.mass = optionsCopy.mass;
        this.scale = optionsCopy.scale;
        this.gravitation = optionsCopy.gravitation;
        this.randomizeRotationDir = optionsCopy.randomizeRotationDir;
        this.randomDrift = optionsCopy.randomDrift;
        this.randomPower = optionsCopy.randomPower;
        this.name = optionsCopy.name;
        this.velocity = optionsCopy.velocity;
        this.velocitySlowDown = optionsCopy.velocitySlowDown;
        this.easeValues = optionsCopy.easeValues;
        this.offset = optionsCopy.offset;
        this.repeat = optionsCopy.repeat;
        this.fpsLimit = optionsCopy.fpsLimit;
        this.particleOptions = optionsCopy;
        this.alphaLinearVanishTime = optionsCopy.alphaLinearVanishTime;
        this.alphaLinearVanishTimeDeviation = optionsCopy.alphaLinearVanishTimeDeviation;
        this.spawnTimeDeviationLimit = optionsCopy.spawnTimeDeviationLimit;
        if (optionsCopy.gravitationAcceleration) {
            this.gravitationAcceleration = optionsCopy.gravitationAcceleration;
        }
        this.activeParticle = this.emit();
    }
    getName() {
        return this.name;
    }
    getParticle() {
        return this.activeParticle;
    }
    setParticleSpan(span) {
        this.span = span;
    }
    setRotation(rotation) {
        this.rotation = rotation;
    }
    setRotationCoefs(rotationCoefs) {
        this.rotationCoefs = rotationCoefs;
    }
    setRotationDir(rotationDir) {
        this.rotationDir = rotationDir;
    }
    setParticleAmountPerEmit(particleAmountPerEmit) {
        this.particleAmountPerEmit = particleAmountPerEmit;
    }
    setRandomizeRotationDir(randomizeRotationDir) {
        this.randomizeRotationDir = randomizeRotationDir;
    }
    setRandomDrift(drift) {
        this.randomDrift = drift;
    }
    setRandomPower(randomPower) {
        this.randomPower = randomPower;
    }
    setGravity(state) {
        this.gravitation = state;
    }
    setVelocity(vector) {
        this.velocity.x = vector.x;
        this.velocity.y = vector.y;
        this.velocity.z = vector.z;
    }
    setVelocitySlowDown(velocitySlowDown) {
        this.velocitySlowDown = velocitySlowDown;
    }
    setEaseValues(easeValues) {
        this.easeValues = easeValues;
    }
    setPosition(vector) {
        this.position.x = vector.x;
        this.position.y = vector.y;
        this.position.z = vector.z;
    }
    setAlpha(alpha) {
        this.alpha = alpha;
    }
    setMass(mass) {
        this.mass = mass;
    }
    setScale(scale) {
        this.scale = scale;
    }
    isActive() {
        return this.active;
    }
    startEmitter() {
        if (!this.enabled)
            return;
        this.active = true;
    }
    stopEmitter(deleteParticles = false) {
        if (deleteParticles) {
            this.activeParticles = [];
        }
        this.active = false;
        this.update();
    }
    setLastEmition(time) {
        this.lastEmition = time;
    }
    emit() {
        return new ParticleObject(this.particleOptions);
    }
    update() {
        this.frames += 1;
        this.removeOldParticles();
        if (this.active)
            this.createNewParticles();
        this.updateParticle();
    }
    createNewParticles() {
        if (this.frames - this.lastEmition > this.emitRate &&
            this.activeParticles.length < this.maxParticleAmount) {
            this.spawnParticle();
        }
    }
    updateParticle() {
        this.activeParticles.forEach(activeParticle => {
            this.applyVelocitySlowDown(activeParticle);
            this.addForces(activeParticle);
            this.addRotation(activeParticle);
            this.applyChangeValues(activeParticle);
        });
        this.updateGeometries();
    }
    removeOldParticles() {
        this.activeParticles = this.activeParticles.filter(particle => {
            if (this.spawnTimeDeviationLimit === undefined && this.alphaLinearVanishTime === undefined) {
                return this.frames - particle.spawnTime < this.particleLife;
            }
            if (Array.isArray(particle.alpha)) {
                for (let i = 0; i < particle.spawnTimeDeviation.length; i++) {
                    const particleSpawnTime = particle.spawnTime + particle.spawnTimeDeviation[i];
                    if ((this.frames - particleSpawnTime >= this.particleLife) && this.alphaLinearVanishTime !== undefined) {
                        const vanishTime = this.alphaLinearVanishTime + particle.alphaLinearVanishTimeDeviation[i];
                        particle.alpha[i] -= particle.alpha[i] / (vanishTime || 1);
                        if (particle.alpha[i] < 0.001) {
                            particle.alpha[i] = 0;
                            particle.alphaLinearVanishTimeDeviation[i] = 0;
                            particle.scale[i] = 0;
                        }
                    }
                }
                if (Math.max(...particle.alpha) > 0.001)
                    return true;
            }
            return false;
        });
    }
    applyVelocitySlowDown(activeParticle) {
        if (activeParticle.velocitySlowDown === undefined)
            return;
        activeParticle.velocity.multiplyScalar(1 - activeParticle.velocitySlowDown);
    }
    applyChangeValues(activeParticle) {
        const easeValues = activeParticle.easeValues;
        if (easeValues === undefined)
            return;
        const frame = this.frames - activeParticle.spawnTime;
        activeParticle.scale = easeValues?.[EaseValuesType.scale]?.frameValues?.[frame] ?? activeParticle.scale;
        activeParticle.alpha = easeValues?.[EaseValuesType.alpha]?.frameValues?.[frame] ?? activeParticle.alpha;
    }
    addForces(activeParticle) {
        if (Array.isArray(activeParticle.position)) {
            this.updateGravity(activeParticle.velocity, activeParticle.mass);
            activeParticle.position = activeParticle.position.map(pos => {
                this.updatePosition(pos, activeParticle.velocity);
                this.simulateRandomDrift(pos);
                return pos;
            });
        }
        else {
            this.updateGravity(activeParticle.velocity, activeParticle.mass);
            this.simulateRandomDrift(activeParticle.position);
            this.updatePosition(activeParticle.position, activeParticle.position);
        }
    }
    updateGravity(velocity, mass) {
        if (!this.gravitation)
            return;
        const gravAccPerFrame = new THREE.Vector3(this.gravitationAcceleration.x * mass, this.gravitationAcceleration.y * mass, this.gravitationAcceleration.z * mass);
        this.addVelocity(velocity, gravAccPerFrame);
    }
    simulateRandomDrift(position) {
        if (!this.randomDrift)
            return;
        let from;
        let to;
        if (typeof this.randomPower === 'number') {
            from = -this.randomPower;
            to = this.randomPower;
        }
        else {
            from = this.randomPower[0];
            to = this.randomPower[1];
        }
        position.x += this.randomNumber(from, to);
        position.y += this.randomNumber(from, to);
        position.z += this.randomNumber(from, to);
    }
    addVelocity(velocity, velocityAddition) {
        velocity.x += velocityAddition.x;
        velocity.y += velocityAddition.y;
        velocity.z += velocityAddition.z;
    }
    updatePosition(position, velocity) {
        position.x += velocity.x / this.fpsLimit;
        position.y += velocity.y / this.fpsLimit;
        position.z += velocity.z / this.fpsLimit;
    }
    addRotation(particle) {
        if (typeof particle.rotation === 'number' &&
            typeof particle.rotationCoefs === 'number' &&
            typeof particle.rotationDir === 'number') {
            particle.rotation += (particle.rotationDir * particle.rotationCoefs);
        }
        else if (typeof particle.rotation !== 'number' &&
            typeof particle.rotationCoefs !== 'number' &&
            typeof particle.rotationDir !== 'number') {
            const coefs = particle.rotationCoefs;
            particle.rotation = particle.rotation.map((rot, i) => {
                return rot + (particle.rotationDir[i] * coefs[i]);
            });
        }
        else {
            throw new Error('Some setup went wrong!');
        }
    }
    randomNumber(min, max) {
        return Math.random() * (max - min) + min;
    }
    updateGeometries() {
        const { position, alpha, scale, rotation } = this.createGeometryArrays();
        const offset = this.createOffsets(scale.length);
        const geometry = this.activeParticle.getGeometry();
        geometry
            .setAttribute('position', new THREE.Float32BufferAttribute(position, 3));
        geometry
            .setAttribute('rotation', new THREE.Float32BufferAttribute(rotation, 1));
        geometry
            .setAttribute('scale', new THREE.Float32BufferAttribute(scale, 1));
        geometry
            .setAttribute('alpha', new THREE.Float32BufferAttribute(alpha, 1));
        geometry
            .setAttribute('offset', new THREE.Float32BufferAttribute(offset, 2));
        geometry.attributes.position.needsUpdate = true;
        geometry.attributes.rotation.needsUpdate = true;
        geometry.attributes.scale.needsUpdate = true;
        geometry.attributes.alpha.needsUpdate = true;
        geometry.attributes.offset.needsUpdate = true;
    }
    createOffsets(length) {
        const offArray = [];
        for (let i = 0; i < length; i++) {
            offArray.push(THREE.MathUtils.randInt(1, this.offset[0]) * (1 / this.repeat[0]));
            offArray.push(THREE.MathUtils.randInt(1, this.offset[1]) * (1 / this.repeat[1]));
        }
        return offArray;
    }
    createGeometryArrays() {
        const position = [];
        const alpha = [];
        const scale = [];
        const rotation = [];
        this.activeParticles.forEach(particle => {
            if (Array.isArray(particle.alpha)) {
                particle.alpha.forEach(alphaVal => {
                    alpha.push(alphaVal);
                });
            }
            else {
                alpha.push(particle.alpha);
            }
            if (Array.isArray(particle.rotation)) {
                particle.rotation.forEach(rotVal => {
                    rotation.push(rotVal);
                });
            }
            else {
                rotation.push(particle.rotation);
            }
            if (Array.isArray(particle.scale)) {
                particle.scale.forEach(scaleVal => {
                    scale.push(scaleVal);
                });
            }
            else {
                scale.push(particle.scale);
            }
            if (Array.isArray(particle.position)) {
                particle.position.forEach(posObject => {
                    const posArray = posObject.toArray();
                    posArray.forEach(val => position.push(val));
                });
            }
            else {
                const posArray = particle.position.toArray();
                posArray.forEach(val => position.push(val));
            }
        });
        return {
            position,
            alpha,
            scale,
            rotation
        };
    }
    spawnParticle() {
        const { position, rotation, rotationCoefs, rotationDir, scale, alpha, velocity, velocitySlowDown, easeValues, mass, alphaLinearVanishTimeDeviation, spawnTimeDeviation } = this.setupParticleEmit();
        this.setLastEmition(this.frames);
        this.activeParticles.push({
            position,
            rotation,
            rotationCoefs,
            rotationDir,
            scale,
            alpha,
            alphaLinearVanishTimeDeviation,
            spawnTimeDeviation,
            spawnTime: this.frames,
            velocity,
            velocitySlowDown,
            easeValues,
            mass
        });
    }
    setupSpawnTimeDeviation() {
        return this.spawnTimeDeviationLimit !== undefined ?
            THREE.MathUtils.randInt(-this.spawnTimeDeviationLimit, this.spawnTimeDeviationLimit) :
            0;
    }
    setupAlphaLinearVanishTimeDeviation() {
        return this.alphaLinearVanishTimeDeviation !== undefined ?
            THREE.MathUtils.randInt(-this.alphaLinearVanishTimeDeviation, this.alphaLinearVanishTimeDeviation) :
            0;
    }
    setupParticleEmit() {
        const particleAmountPerEmit = this.particleAmountPerEmit;
        const position = [];
        const rotation = [];
        const rotationCoefs = [];
        const rotationDir = [];
        const scale = [];
        const alpha = [];
        const alphaLinearVanishTimeDeviation = [];
        const spawnTimeDeviation = [];
        let velocity = new THREE.Vector3();
        let velocitySlowDown = 0;
        let easeValues;
        let mass = 0;
        const spawnParticles = typeof particleAmountPerEmit === 'number' ?
            particleAmountPerEmit :
            this.randomNumber(particleAmountPerEmit[0], particleAmountPerEmit[1]);
        for (let i = 0; i < spawnParticles; i++) {
            const alphaLinearVanishTimeDevi = this.setupAlphaLinearVanishTimeDeviation();
            alphaLinearVanishTimeDeviation.push(alphaLinearVanishTimeDevi);
            const spawnTimeDevi = this.setupSpawnTimeDeviation();
            spawnTimeDeviation.push(spawnTimeDevi);
            const pos = this.setUpPosition();
            position.push(pos);
            const rot = this.setRotationMatrixes();
            rotation.push(rot);
            const rotCoefs = this.setRotationCoefsMatrixes();
            rotationCoefs.push(rotCoefs);
            const rotDir = this.setRotationDirMatrixes();
            rotationDir.push(rotDir);
            const scl = this.scaleParticle();
            scale.push(scl);
            const alp = this.setParticleAlpha();
            alpha.push(alp);
            velocity = this.setParticleVelocity();
            velocitySlowDown = this.setParticleVelocitySlowDown();
            easeValues = this.setParticleEaseValues(scl, alp);
            mass = this.setParticleMass();
        }
        return {
            alphaLinearVanishTimeDeviation,
            spawnTimeDeviation,
            position,
            rotation,
            rotationCoefs,
            rotationDir,
            scale,
            alpha,
            velocity,
            velocitySlowDown,
            easeValues,
            mass
        };
    }
    setUpPosition() {
        const basePosition = this.position;
        const particleSpan = this.span;
        const x = (Math.random() * particleSpan.x) + basePosition.x;
        const y = (Math.random() * particleSpan.y) + basePosition.y;
        const z = (Math.random() * particleSpan.z) + basePosition.z;
        return new THREE.Vector3(x, y, z);
    }
    setRotationMatrixes() {
        const setupRotation = this.rotation;
        const isNum = typeof setupRotation === 'number';
        return isNum ? setupRotation : this.randomNumber(setupRotation[0], setupRotation[1]);
    }
    setRotationCoefsMatrixes() {
        const setupRotationCoefs = this.rotationCoefs;
        const isNum = typeof setupRotationCoefs === 'number';
        return isNum ? setupRotationCoefs : this.randomNumber(setupRotationCoefs[0], setupRotationCoefs[1]);
    }
    setRotationDirMatrixes() {
        const randomValue = Math.round(Math.random()) === 0 ? 1 : -1;
        const randomDirection = this.randomizeRotationDir ? randomValue : 1;
        return this.rotationDir * randomDirection;
    }
    scaleParticle() {
        const setupScale = this.scale;
        const isNum = typeof setupScale === 'number';
        return isNum ? setupScale : this.randomNumber(setupScale[0], setupScale[1]);
    }
    setParticleAlpha() {
        const setupAlpha = this.alpha;
        const isNum = typeof setupAlpha === 'number';
        return isNum ? setupAlpha : this.randomNumber(setupAlpha[0], setupAlpha[1]);
    }
    setParticleVelocity() {
        const setupVelocity = new THREE.Vector3(this.velocity.x, this.velocity.y, this.velocity.z);
        return setupVelocity;
    }
    setParticleVelocitySlowDown() {
        return this.velocitySlowDown;
    }
    setFrameValuesForEaseValues(easeType, startValue) {
        if (this.easeValues === undefined)
            return {};
        const easeValue = this.easeValues[easeType];
        if (easeValue && easeValue.easeFunction !== undefined && easeValue.endValue !== undefined) {
            easeValue.frameValues = [];
            const objToChange = { value: startValue };
            const helpTween = gsap.to(objToChange, {
                value: easeValue.endValue,
                duration: 1,
                ease: easeValue.easeFunction
            }).pause();
            for (let i = 0; i < this.particleLife; i += 1) {
                helpTween.progress(i / (this.particleLife - 1));
                const valueToAdd = objToChange.value;
                easeValue.frameValues.push(valueToAdd);
            }
        }
        return easeValue;
    }
    setParticleEaseValues(scale, alpha) {
        return {
            [EaseValuesType.scale]: this.setFrameValuesForEaseValues(EaseValuesType.scale, scale),
            [EaseValuesType.alpha]: this.setFrameValuesForEaseValues(EaseValuesType.alpha, alpha)
        };
    }
    setParticleMass() {
        const setupMass = this.mass;
        const isNum = typeof setupMass === 'number';
        return isNum ? setupMass : this.randomNumber(setupMass[0], setupMass[1]);
    }
}
//# sourceMappingURL=ParticleEmitter.js.map